import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { editPriceListOffer, endPriceListOffer, replacePriceListPromotion, endPriceListPromotion } from '../../api';
import { formatPesoAmount } from '../../utils/money';
import BranchOfferDialog from './BranchOfferDialog';

const dateLabel = (value) => new Date(value).toLocaleString('es-CL', { timeZone: 'America/Santiago', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const buttonClass = 'min-h-9 rounded-md border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50';
const BranchOffersContext = createContext(null);

export const applyBranchOfferAction = async (group, action, branchIds, payload) => {
  const results = [];
  for (const member of group.members.filter((entry) => branchIds.includes(entry.branchId))) {
    try {
      if (!member.canUpdate) throw new Error('No tienes permiso para modificar esta sucursal.');
      if (group.kind === 'offer') {
        const scope = { expectedEnabledAt: member.expectedEnabledAt };
        if (action === 'edit') {
          const { quantity, ...terms } = payload;
          await editPriceListOffer(member.branchId, group.varietyPriceId, { ...terms, ...scope, acknowledgePolicyWarnings: true });
        } else await endPriceListOffer(member.branchId, group.varietyPriceId, scope);
      } else if (action === 'edit') {
        const { sellingPrice, ...terms } = payload;
        await replacePriceListPromotion(member.branchId, member.promotionId, { ...terms, type: group.type, promotionPrice: sellingPrice });
      } else await endPriceListPromotion(member.branchId, member.promotionId);
      results.push({ member, success: true });
    } catch (error) { results.push({ member, success: false, error: error?.response?.data?.message || error.message || 'No se pudo guardar.' }); }
  }
  return results;
};

export function BranchOffersProvider({ overview, loading, onRefresh, onNotice, disabled = false, now = Date.now(), scopeKey = 'default', children }) {
  const [targetState, setTarget] = useState(null);
  const target = targetState?.scopeKey === scopeKey ? targetState : null;
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState('');
  const generationRef = useRef(0);
  useEffect(() => {
    setTarget(null);
    setSaving(false);
    setActionError('');
    return () => { generationRef.current += 1; };
  }, [scopeKey]);
  const open = (group, action) => { setActionError(''); onNotice?.(''); setTarget({ group, action, scopeKey }); };
  const submit = async (branchIds, payload) => {
    if (saving || !target) return;
    const generation = generationRef.current;
    setSaving(true);
    setActionError('');
    const results = await applyBranchOfferAction(target.group, target.action, branchIds, payload);
    if (generation !== generationRef.current) return;
    const failed = results.filter((result) => !result.success);
    const saved = results.filter((result) => result.success).length;
    onNotice?.(saved ? `Cambios aplicados en ${saved} sucursal${saved === 1 ? '' : 'es'}.` : '');
    if (failed.length) {
      setActionError(`${saved ? `${saved} sucursal(es) guardadas. ` : ''}Reintenta sólo las pendientes:\n${failed.map((result) => `${result.member.branchName}: ${result.error}`).join('\n')}`);
      setTarget({ ...target, retry: (target.retry || 0) + 1, group: { ...target.group, members: failed.map((result) => result.member) }, draft: payload });
    } else setTarget(null);
    await onRefresh();
    if (generation === generationRef.current) setSaving(false);
  };
  const offersByFormat = useMemo(() => {
    const result = new Map();
    if (overview?.isMainBranch) {
      for (const offer of overview.offers || []) {
        if (!(Date.parse(offer.expiresAt) > now)) continue;
        const id = String(offer.varietyPriceId);
        if (!result.has(id)) result.set(id, []);
        result.get(id).push(offer);
      }
    }
    return result;
  }, [overview, now]);
  return <BranchOffersContext.Provider value={{ offersByFormat, open, disabled: disabled || saving || loading, now }}>
    {children}
    {target && <BranchOfferDialog key={`${target.group.key}:${target.action}:${target.retry || 0}`} target={target} saving={saving} error={actionError} onClose={() => setTarget(null)} onSubmit={submit} />}
  </BranchOffersContext.Provider>;
}

export default function BranchOfferRows({ formats, colSpan, label }) {
  const context = useContext(BranchOffersContext);
  if (!context) return null;
  const { offersByFormat, open, disabled, now } = context;
  const offers = formats.flatMap((format) => (offersByFormat.get(String(format.id)) || [])
    .map((group) => ({ group, formatName: format.productFormatName || format.clientType || 'Oferta' })));
  return <>{offers.map(({ group, formatName }) => <tr key={group.key} data-branch-offer={group.key}>
    <td colSpan={colSpan} className="py-1 pl-11 pr-3">
      <article aria-label={`Oferta de ${label}: ${formatName}`} className="border-l-2 border-amber-300 bg-amber-50/50 px-3 py-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div><p className="m-0 text-xs font-semibold text-slate-900" title={group.label}>{formatName}</p>
            <p className="mb-0 mt-1 text-sm text-amber-950">{Date.parse(group.startsAt) > now ? 'Programada' : 'Vigente'} · {group.type === 'pack' ? `${group.quantity.toLocaleString('es-CL')} por `
              : group.type === 'weight_threshold' ? `Desde ${group.quantity.toLocaleString('es-CL')} kg: ` : ''}
            <strong>{group.sellingPrice == null ? 'Sin precio' : `$${formatPesoAmount(group.sellingPrice)}`}</strong>{group.type !== 'pack' && ` / ${group.metricUnit}`}</p>
          </div>
          {group.members.some((member) => member.canUpdate) && <div className="flex flex-wrap gap-2">
            <button type="button" className={buttonClass} disabled={disabled} onClick={() => open(group, 'edit')}>Editar</button>
            <button type="button" className={buttonClass} disabled={disabled} onClick={() => open(group, 'remove')}>Quitar de sucursales</button>
            <button type="button" className={buttonClass} disabled={disabled || group.members.some((member) => !member.canUpdate)} onClick={() => open(group, 'delete')}>Eliminar de todas</button>
          </div>}
        </div>
        <div className="mt-2 flex flex-wrap gap-1">{group.members.map((member) => <span key={`${member.branchId}:${member.promotionId || ''}`} className={`rounded-full border px-2 py-0.5 text-xs ${member.isAvailable ? 'border-blue-200 bg-blue-50 text-blue-800' : 'border-slate-300 bg-slate-100 text-slate-600'}`}>
          {member.branchName}{!member.isAvailable && ' · no disponible'}{group.maxQuantity != null && ` · ${member.soldQuantity.toLocaleString('es-CL')} vendidos`}
        </span>)}</div>
        <p className="mb-0 mt-1 text-xs text-slate-600">{dateLabel(group.startsAt)} – {dateLabel(group.expiresAt)} (Chile)
          {group.maxQuantity != null && ` · stock por sucursal: ${group.maxQuantity.toLocaleString('es-CL')} ${group.metricUnit} · tolerancia: ${Number(group.tolerance || 0).toLocaleString('es-CL')}`}</p>
      </article>
    </td>
  </tr>)}</>;
}
